import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { DISTRICTS, PLATFORMS, PLATFORM_COLORS } from "@/lib/images";
import { SiteNav } from "@/components/public/SiteNav";

const BD_MOBILE = /^01[3-9]\d{8}$/;

function SuccessScreen({ result, settings }) {
  const wa = (settings?.whatsapp_number || "").replace(/[^0-9]/g, "");
  const showWa = wa && !result.has_email;

  return (
    <motion.div
      data-testid="success-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[60] bg-sylhet-forest grain flex items-center justify-center px-6"
    >
      {/* confetti dots */}
      {Array.from({ length: 18 }).map((_, i) => (
        <motion.span
          key={i}
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: [-40, 120 + (i % 5) * 40], opacity: [0, 1, 0] }}
          transition={{ duration: 2.4, delay: i * 0.08, repeat: Infinity, repeatDelay: 1.5 }}
          className="absolute top-10 w-2 h-2 rounded-full"
          style={{ left: `${(i * 5.5 + 8) % 100}%`, background: i % 2 ? "#E0A52E" : "#F42A41" }}
        />
      ))}

      <div className="relative z-10 text-center max-w-lg">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 14 }}
          className="w-24 h-24 mx-auto rounded-full bg-sylhet-green grid place-items-center shadow-2xl"
        >
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.25 }}>
            <Check className="text-cream" size={48} strokeWidth={3} />
          </motion.div>
        </motion.div>

        <h2 className="font-display text-3xl md:text-4xl font-bold text-cream mt-8">Entry received!</h2>
        <p className="font-bn text-cream/75 mt-3 text-lg">আপনার রিল জমা হয়েছে। ধন্যবাদ!</p>

        <div className="mt-8 inline-block rounded-2xl border border-cream/20 bg-cream/5 px-8 py-5">
          <div className="text-cream/50 text-xs uppercase tracking-[0.2em]">Your reference</div>
          <div data-testid="entry-ref" className="font-mono text-3xl md:text-4xl font-bold text-sylhet-gold mt-1">
            {result.entry_ref}
          </div>
        </div>

        <p className="text-cream/60 mt-6 max-w-md mx-auto">
          {result.has_email
            ? "Keep this reference safe. We'll be in touch by email about the next steps."
            : "Save this reference number. Our team will use it to identify your entry."}
        </p>

        <div className="flex flex-wrap items-center justify-center gap-3 mt-9">
          {showWa && (
            <a
              data-testid="whatsapp-button"
              href={`https://wa.me/${wa}?text=${encodeURIComponent(`Hi, my Made in Sylhet entry ref is ${result.entry_ref}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#25D366] text-white font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              <MessageCircle size={20} /> Contact on WhatsApp
            </a>
          )}
          <Link to="/" data-testid="success-home" className="px-7 py-3.5 rounded-full border-2 border-cream/40 text-cream font-medium hover:bg-cream/10 transition-colors">
            Back to home
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

export default function Submit() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    name: "", address: "", mobile: "", video_url: "", email: "",
    district: "", platform: "", note: "", company: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const mobileOk = useMemo(() => BD_MOBILE.test(form.mobile.replace(/[\s-]/g, "")), [form.mobile]);

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "নাম লিখুন";
    if (form.address.trim().length < 2) er.address = "ঠিকানা লিখুন";
    if (!BD_MOBILE.test(form.mobile.replace(/[\s-]/g, ""))) er.mobile = "সঠিক মোবাইল নম্বর দিন (01XXXXXXXXX)";
    if (!/^https?:\/\//.test(form.video_url.trim())) er.video_url = "একটি সঠিক ভিডিও লিংক দিন";
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) er.email = "সঠিক ইমেইল দিন";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("কিছু তথ্য ঠিক করুন");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, mobile: form.mobile.replace(/[\s-]/g, "") };
      Object.keys(payload).forEach((k) => payload[k] === "" && (payload[k] = k === "company" ? "" : undefined));
      const { data } = await api.post("/submit", payload);
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "জমা দেওয়া যায়নি");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b-2 border-sylhet-forest/20 py-3.5 text-lg focus:outline-none focus:border-sylhet-green transition-colors placeholder:text-charcoal/30";

  return (
    <div className="min-h-screen bg-cream">
      <SiteNav />
      <AnimatePresence>{result && <SuccessScreen result={result} settings={settings} />}</AnimatePresence>

      <div className="max-w-3xl mx-auto px-6 md:px-8 pt-28 md:pt-36 pb-24">
        <div className="mb-10">
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-sylhet-green">Submission</span>
          <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight mt-3">Submit your reel</h1>
          <p className="font-bn text-charcoal/60 mt-3 text-lg">নিচের তথ্যগুলো পূরণ করুন। তারকা (*) চিহ্নিত ঘরগুলো আবশ্যক।</p>
        </div>

        <form onSubmit={submit} className="space-y-9" data-testid="submission-form">
          {/* honeypot */}
          <input
            type="text" tabIndex={-1} autoComplete="off"
            value={form.company} onChange={set("company")}
            className="hidden" aria-hidden="true" name="company"
          />

          <div>
            <label className="block text-sm font-semibold text-charcoal/70 mb-1">Full name *</label>
            <input data-testid="field-name" value={form.name} onChange={set("name")} placeholder="আপনার পূর্ণ নাম" className={inputCls} />
            {errors.name && <p className="text-sylhet-red text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-charcoal/70 mb-1">Address *</label>
            <input data-testid="field-address" value={form.address} onChange={set("address")} placeholder="গ্রাম / এলাকা, উপজেলা, জেলা" className={inputCls} />
            {errors.address && <p className="text-sylhet-red text-sm mt-1">{errors.address}</p>}
          </div>

          <div className="grid sm:grid-cols-2 gap-8">
            <div>
              <label className="block text-sm font-semibold text-charcoal/70 mb-1">Mobile number *</label>
              <input data-testid="field-mobile" value={form.mobile} onChange={set("mobile")} placeholder="01XXXXXXXXX" inputMode="numeric" className={inputCls} />
              {form.mobile && (mobileOk
                ? <p className="text-sylhet-green text-sm mt-1">✓ ঠিক আছে</p>
                : !errors.mobile && <p className="text-charcoal/40 text-sm mt-1">11 ডিজিটের নম্বর</p>)}
              {errors.mobile && <p className="text-sylhet-red text-sm mt-1">{errors.mobile}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-charcoal/70 mb-1">Email <span className="text-charcoal/40">(optional)</span></label>
              <input data-testid="field-email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
              {errors.email && <p className="text-sylhet-red text-sm mt-1">{errors.email}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-charcoal/70 mb-1">Video link *</label>
            <input data-testid="field-video" value={form.video_url} onChange={set("video_url")} placeholder="https://… (YouTube, Facebook, Instagram, TikTok)" className={inputCls} />
            {errors.video_url && <p className="text-sylhet-red text-sm mt-1">{errors.video_url}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-charcoal/70 mb-3">District <span className="text-charcoal/40">(optional)</span></label>
            <div className="flex flex-wrap gap-2.5">
              {DISTRICTS.map((d) => (
                <button
                  key={d} type="button" data-testid={`district-${d}`}
                  onClick={() => setForm((f) => ({ ...f, district: f.district === d ? "" : d }))}
                  className={`rounded-full px-5 py-2 border text-sm font-medium transition-all ${
                    form.district === d ? "bg-sylhet-green text-cream border-sylhet-green" : "border-sylhet-forest/25 text-charcoal/70 hover:border-sylhet-green"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-charcoal/70 mb-3">Platform <span className="text-charcoal/40">(optional)</span></label>
            <div className="flex flex-wrap gap-2.5">
              {PLATFORMS.map((p) => {
                const active = form.platform === p;
                return (
                  <button
                    key={p} type="button" data-testid={`platform-${p}`}
                    onClick={() => setForm((f) => ({ ...f, platform: active ? "" : p }))}
                    className="rounded-full px-5 py-2 border text-sm font-medium transition-all"
                    style={active
                      ? { background: PLATFORM_COLORS[p], color: p === "TikTok" ? "#fff" : "#fff", borderColor: PLATFORM_COLORS[p] }
                      : { borderColor: "rgba(11,61,46,0.25)", color: "rgba(22,22,22,0.7)" }}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-charcoal/70 mb-1">Note <span className="text-charcoal/40">(optional)</span></label>
            <textarea data-testid="field-note" value={form.note} onChange={set("note")} rows={3} placeholder="আপনার রিল সম্পর্কে কিছু বলুন…" className={`${inputCls} resize-none`} />
          </div>

          <button
            type="submit" data-testid="submit-button" disabled={loading}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full bg-sylhet-red text-cream font-semibold text-lg hover:-translate-y-0.5 hover:shadow-xl transition-all disabled:opacity-60"
          >
            {loading ? <><Loader2 className="animate-spin" size={20} /> জমা হচ্ছে…</> : "Submit entry →"}
          </button>
          <p className="text-charcoal/40 text-sm">No submission fee. We never share your contact details publicly.</p>
        </form>
      </div>
    </div>
  );
}
